import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import dayjs from 'dayjs';
import 'dayjs/locale/pt';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardTabs from '../components/dashboard/DashboardTabs';
import { dashboardTabs } from '../components/dashboard/dashboardMockData';
import { useUser } from '../hooks/useUser';
import { getCallableUsers, searchCallableUsers } from '../services/call';
import {
    createAppointmentAdmin,
    listAppointments,
    updateAppointmentAdmin
} from '../services/appointments';
import { createClinicalNote, fetchClinicalNote } from '../services/clinicalNotes';
import { callConfig } from '../configs/call';
import { fetchUserByRole, updateUserByRole } from '../services/users';

dayjs.locale('pt');

const primaryButtonSx = {
    minHeight: 46,
    px: 2.6,
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '16px',
    bgcolor: '#D63731',
    color: '#FFFFFF',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#BF2F2A',
        boxShadow: 'none'
    }
};

const secondaryButtonSx = {
    minHeight: 46,
    px: 2.4,
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '16px',
    color: '#3D3D3D',
    borderColor: '#D9D9D9',
    borderWidth: '2px',
    '&:hover': {
        bgcolor: '#F7F7F7',
        borderColor: '#CFCFCF',
        borderWidth: '2px'
    }
};

const dialogPaperSx = {
    borderRadius: '22px',
    boxShadow: '0px 10px 30px rgba(61, 61, 61, 0.12)'
};

const dialogTitleSx = {
    px: 3,
    pt: 3,
    pb: 1.5,
    fontWeight: 700,
    color: '#3D3D3D'
};

const dialogContentSx = {
    px: 3,
    pb: 1,
    pt: 0.5,
    display: 'grid',
    gap: 12
};

const dialogActionsSx = {
    px: 3,
    pb: 2.5,
    pt: 1
};

const weekdayOptions = [
    { key: 'mon', label: 'SEG' },
    { key: 'tue', label: 'TER' },
    { key: 'wed', label: 'QUA' },
    { key: 'thu', label: 'QUI' },
    { key: 'fri', label: 'SEX' },
    { key: 'sat', label: 'SAB' },
    { key: 'sun', label: 'DOM' }
];

const durationOptions = [30, 45, 60];

const defaultAvailability = {
    activeDays: ['mon', 'tue', 'wed', 'thu', 'fri'],
    notes: 'SEG-QUI | 09:00-13:00 14:00-18:00\nSEX | 14:00-18:00'
};

const AVAILABILITY_ACTIVE_DAYS_KEY = 'availability_active_days';
const AVAILABILITY_NOTES_KEY = 'availability_notes';

const normalizeAvailability = (userData = {}) => {
    const rawDays = userData?.[AVAILABILITY_ACTIVE_DAYS_KEY];
    const parsedDays = Array.isArray(rawDays)
        ? rawDays
        : typeof rawDays === 'string'
            ? rawDays
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean)
            : [];
    const validDays = parsedDays.filter((day) => weekdayOptions.some((option) => option.key === day));

    return {
        activeDays: validDays.length ? validDays : defaultAvailability.activeDays,
        notes:
            typeof userData?.[AVAILABILITY_NOTES_KEY] === 'string'
                ? userData[AVAILABILITY_NOTES_KEY]
                : defaultAvailability.notes
    };
};

const createInitialAppointmentForm = () => ({
    caregiver: '',
    date: dayjs().startOf('day'),
    time: '10:00',
    duration: 45
});

const formatAgendaDayTitle = (date) => date.locale('pt').format('D MMMM').toUpperCase();

const formatAgendaWeekday = (date) => {
    const label = date.locale('pt').format('dddd');
    return label.charAt(0).toUpperCase() + label.slice(1);
};

const formatAppointmentQuickLabel = (date, start, end) => {
    if (!date || !start || !end) {
        return '';
    }

    return `${date.locale('pt').format('ddd').toUpperCase()} | ${start} - ${end}`;
};

const parseAppointmentNames = (title) => {
    if (!title) {
        return {
            headline: 'Marcação',
            secondary: ''
        };
    }

    const parts = title
        .split(' e ')
        .map((item) => item.trim())
        .filter(Boolean);

    if (parts.length >= 2) {
        return {
            headline: `${parts[0]} e ${parts[1]}`,
            secondary: ''
        };
    }

    return {
        headline: title,
        secondary: ''
    };
};

const isSameDay = (first, second) =>
    Boolean(first) && Boolean(second) && first.startOf('day').isSame(second.startOf('day'));

const isBetweenDays = (value, start, end) => {
    if (!value || !start || !end) {
        return false;
    }

    const ts = value.startOf('day').valueOf();
    return ts > start.startOf('day').valueOf() && ts < end.startOf('day').valueOf();
};

const rangeLengthInDays = (start, end) => {
    if (!start || !end) {
        return 0;
    }

    return end.startOf('day').diff(start.startOf('day'), 'day') + 1;
};

const buildAgendaFromAppointments = (appointments = [], endDate = null) => {
    const grouped = new Map();

    appointments.forEach((item) => {
        const ts = dayjs(item?.ts);
        if (!ts.isValid()) {
            return;
        }

        if (endDate && ts.startOf('day').isAfter(endDate.startOf('day'))) {
            return;
        }

        const dateKey = ts.format('YYYY-MM-DD');
        const entry = {
            id: item?.uuid || `${dateKey}-${item?.caregiver?.uuid || item?.ts}`,
            uuid: item?.uuid || null,
            start: ts.format('HH:mm'),
            end: ts.add(Number(item?.slot_duration || 0), 'minute').format('HH:mm'),
            slotDuration: Number(item?.slot_duration || 0),
            title: item?.caregiver?.name || 'Sem cuidador',
            ts: item?.ts || null,
            status: item?.status || null,
            caregiver: item?.caregiver || null,
            clinician: item?.clinician || null,
            note: item?.note || null,
            call: item?.call || null
        };

        if (!grouped.has(dateKey)) {
            grouped.set(dateKey, {
                date: ts.startOf('day'),
                entries: []
            });
        }

        grouped.get(dateKey).entries.push(entry);
    });

    return Array.from(grouped.values())
        .map((day) => ({
            ...day,
            entries: [...day.entries].sort((a, b) => a.start.localeCompare(b.start))
        }))
        .sort((a, b) => a.date.valueOf() - b.date.valueOf());
};

const appointmentStatusMap = {
    FREE: {
        label: 'Livre',
        sx: {
            bgcolor: '#EEF2F7',
            color: '#52606D'
        }
    },
    SCHEDULED: {
        label: 'Por realizar',
        sx: {
            bgcolor: 'rgba(214, 55, 49, 0.10)',
            color: '#B02A25'
        }
    },
    ONGOING: {
        label: 'Em curso',
        sx: {
            bgcolor: 'rgba(242, 201, 76, 0.18)',
            color: '#8A6A00'
        }
    },
    COMPLETED: {
        label: 'Realizada',
        sx: {
            bgcolor: 'rgba(38, 166, 91, 0.14)',
            color: '#1F7A45'
        }
    },
    CANCELLED: {
        label: 'Cancelada',
        sx: {
            bgcolor: 'rgba(120, 120, 120, 0.14)',
            color: '#6B6B6B'
        }
    },
    UNKNOWN: {
        label: 'Sem estado',
        sx: {
            bgcolor: '#F3F3F3',
            color: '#7A7A7A'
        }
    }
};

const getAppointmentStatusMeta = (status) => appointmentStatusMap[status] || appointmentStatusMap.UNKNOWN;

const isAppointmentOnCurrentDay = (entry) => {
    const appointmentDate = dayjs(entry?.ts);
    if (!appointmentDate.isValid()) {
        return false;
    }

    return appointmentDate.isSame(dayjs(), 'day');
};

const AppointmentRangeDay = (props) => {
    const { day, outsideCurrentMonth, rangeStart, rangeEnd, ...other } = props;
    const isStart = isSameDay(day, rangeStart);
    const isEnd = isSameDay(day, rangeEnd);
    const inRange = isBetweenDays(day, rangeStart, rangeEnd);

    return (
        <Box
            sx={{
                position: 'relative',
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&::before':
                    inRange
                        ? {
                              content: '""',
                              position: 'absolute',
                              inset: '4px -6px',
                              bgcolor: 'rgba(214, 55, 49, 0.12)',
                              zIndex: 0
                          }
                        : undefined
            }}
        >
            <PickersDay
                {...other}
                day={day}
                outsideCurrentMonth={outsideCurrentMonth}
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    color: isStart || isEnd ? '#FFFFFF' : '#3D3D3D',
                    bgcolor: isStart || isEnd ? '#D63731 !important' : 'transparent',
                    fontWeight: isStart || isEnd || inRange ? 700 : 500,
                    '&:hover': {
                        bgcolor: isStart || isEnd ? '#BF2F2A !important' : 'rgba(214, 55, 49, 0.08)'
                    }
                }}
            />
        </Box>
    );
};

const Appointments = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useUser();
    const [teamMembers, setTeamMembers] = useState([]);
    const [teamMemberSearchInput, setTeamMemberSearchInput] = useState('');
    const [teamMemberSearchOptions, setTeamMemberSearchOptions] = useState([]);
    const [teamMemberSearchLoading, setTeamMemberSearchLoading] = useState(false);
    const [caregivers, setCaregivers] = useState([]);
    const [selectedAgendaRole, setSelectedAgendaRole] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [calendarRangeDraft, setCalendarRangeDraft] = useState({
        start: dayjs().startOf('day'),
        end: dayjs().startOf('day').add(6, 'day')
    });
    const [appliedStartDate, setAppliedStartDate] = useState(dayjs().startOf('day'));
    const [appliedEndDate, setAppliedEndDate] = useState(dayjs().startOf('day').add(6, 'day'));
    const [calendarSelectionError, setCalendarSelectionError] = useState('');
    const [availability, setAvailability] = useState(defaultAvailability);
    const [availabilityDraft, setAvailabilityDraft] = useState(defaultAvailability);
    const [editingAvailability, setEditingAvailability] = useState(false);
    const [availabilityLoading, setAvailabilityLoading] = useState(false);
    const [availabilitySaving, setAvailabilitySaving] = useState(false);
    const [availabilityError, setAvailabilityError] = useState('');
    const [selectedAgendaRoleUserData, setSelectedAgendaRoleUserData] = useState({});
    const [showCompletedOnly, setShowCompletedOnly] = useState(false);
    const [newAppointmentDialogOpen, setNewAppointmentDialogOpen] = useState(false);
    const [appointmentForm, setAppointmentForm] = useState(createInitialAppointmentForm());
    const [appointmentSubmitting, setAppointmentSubmitting] = useState(false);
    const [appointmentError, setAppointmentError] = useState('');
    const [appointmentSuccess, setAppointmentSuccess] = useState('');
    const [caregiverSearchInput, setCaregiverSearchInput] = useState('');
    const [caregiverSearchOptions, setCaregiverSearchOptions] = useState([]);
    const [caregiverSearchLoading, setCaregiverSearchLoading] = useState(false);
    const [appointments, setAppointments] = useState([]);
    const [loadingAppointments, setLoadingAppointments] = useState(false);
    const [appointmentsError, setAppointmentsError] = useState('');
    const [appointmentsReloadKey, setAppointmentsReloadKey] = useState(0);
    const [selectedAppointmentDetails, setSelectedAppointmentDetails] = useState(null);
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [noteDialogMode, setNoteDialogMode] = useState('create');
    const [noteContent, setNoteContent] = useState('');
    const [notePriviledged, setNotePriviledged] = useState(false);
    const [noteLoading, setNoteLoading] = useState(false);
    const [noteSubmitting, setNoteSubmitting] = useState(false);
    const [noteError, setNoteError] = useState('');
    const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
    const [rescheduleSubmitting, setRescheduleSubmitting] = useState(false);
    const [rescheduleError, setRescheduleError] = useState('');
    const [rescheduleForm, setRescheduleForm] = useState({
        date: dayjs().startOf('day'),
        time: '10:00',
        duration: 45
    });
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [statusSubmitting, setStatusSubmitting] = useState(false);
    const [statusError, setStatusError] = useState('');
    const [manualStatusValue, setManualStatusValue] = useState('SCHEDULED');
    const initialAgendaRoleUuid = location.state?.agendaRoleUuid || '';
    const focusAppointmentUuid = location.state?.focusAppointmentUuid || '';
    const focusDateIso = location.state?.focusDateIso || '';

    useEffect(() => {
        let cancelled = false;

        const loadTeamMembers = async () => {
            if (!user.selectedRole) {
                return;
            }

            try {
                const currentUserId =
                    callConfig.mode === 'local-dev' ? callConfig.devUserId : user.selectedRole;
                const response = await getCallableUsers({
                    currentUserId,
                    userRoleUuid: user.selectedRole,
                    roleFilters: ['ACCOUNT_ADMIN', 'CLINIC_CARE_GIVER'],
                    paginationLimit: 50,
                    includeCurrentUser: true
                });

                if (cancelled) {
                    return;
                }

                const users = Array.isArray(response?.users) ? response.users : [];
                setTeamMembers(users);
                setTeamMemberSearchOptions(users.slice(0, 12));
                setSelectedAgendaRole((prev) => {
                    if (initialAgendaRoleUuid && users.some((item) => item.userId === initialAgendaRoleUuid)) {
                        return initialAgendaRoleUuid;
                    }
                    if (prev && users.some((item) => item.userId === prev)) {
                        return prev;
                    }
                    return (
                        users.find((item) => item.userId === user.selectedRole)?.userId ||
                        users[0]?.userId ||
                        ''
                    );
                });
            } catch {
                if (cancelled) {
                    return;
                }

                const fallbackUser = {
                    userId: user.selectedRole || 'self',
                    displayName:
                        user.roles?.find((role) => role.uuid === user.selectedRole)?.name ||
                        'Minha Agenda'
                };
                setTeamMembers([fallbackUser]);
                setTeamMemberSearchOptions([fallbackUser]);
                setSelectedAgendaRole(fallbackUser.userId);
            }
        };

        void loadTeamMembers();

        return () => {
            cancelled = true;
        };
    }, [initialAgendaRoleUuid, user.roles, user.selectedRole]);

    useEffect(() => {
        if (!focusAppointmentUuid && !focusDateIso) {
            return;
        }

        const appointmentDay = dayjs(focusDateIso).startOf('day');
        if (!appointmentDay.isValid()) {
            return;
        }

        if (initialAgendaRoleUuid) {
            setSelectedAgendaRole(initialAgendaRoleUuid);
        }

        setCalendarRangeDraft({
            start: appointmentDay,
            end: appointmentDay
        });
        setAppliedStartDate(appointmentDay);
        setAppliedEndDate(appointmentDay);
        setSearchQuery('');
    }, [focusAppointmentUuid, focusDateIso, initialAgendaRoleUuid]);

    useEffect(() => {
        let cancelled = false;
        let intervalId = null;

        const loadCaregivers = async () => {
            if (!user.selectedRole) {
                return;
            }

            try {
                const currentUserId =
                    callConfig.mode === 'local-dev' ? callConfig.devUserId : user.selectedRole;
                const response = await getCallableUsers({
                    currentUserId,
                    userRoleUuid: user.selectedRole,
                    roleFilters: ['PATIENT_CARE_GIVER'],
                    paginationLimit: 50,
                    includeCurrentUser: true
                });

                if (cancelled) {
                    return;
                }

                setCaregivers(Array.isArray(response?.users) ? response.users : []);
            } catch {
                // Keep the last known presence state if a background refresh fails.
            }
        };

        void loadCaregivers();
        intervalId = setInterval(() => {
            void loadCaregivers();
        }, 30000);

        return () => {
            cancelled = true;
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [initialAgendaRoleUuid, user.selectedRole]);

    useEffect(() => {
        if (!newAppointmentDialogOpen || !user.selectedRole) {
            return undefined;
        }

        let cancelled = false;
        const query = caregiverSearchInput.trim();

        const runSearch = async () => {
            setCaregiverSearchLoading(true);
            try {
                const response = query
                    ? await searchCallableUsers({
                        query,
                        currentUserId: user.selectedRole,
                        userRoleUuid: user.selectedRole,
                        roleFilters: ['PATIENT_CARE_GIVER'],
                        paginationLimit: 12,
                        includeCurrentUser: true
                    })
                    : {
                        users: caregivers.slice(0, 12)
                    };

                if (cancelled) {
                    return;
                }

                const nextOptions = Array.isArray(response?.users) ? response.users : [];
                setCaregiverSearchOptions((prev) => {
                    if (
                        appointmentForm.caregiver &&
                        !nextOptions.some((item) => item.userId === appointmentForm.caregiver)
                    ) {
                        const selected =
                            caregivers.find((item) => item.userId === appointmentForm.caregiver) ||
                            prev.find((item) => item.userId === appointmentForm.caregiver);
                        return selected ? [selected, ...nextOptions] : nextOptions;
                    }
                    return nextOptions;
                });
            } catch {
                if (!cancelled) {
                    setCaregiverSearchOptions([]);
                }
            } finally {
                if (!cancelled) {
                    setCaregiverSearchLoading(false);
                }
            }
        };

        const timeoutId = setTimeout(() => {
            void runSearch();
        }, query ? 250 : 0);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [
        appointmentForm.caregiver,
        caregiverSearchInput,
        caregivers,
        newAppointmentDialogOpen,
        user.selectedRole
    ]);

    useEffect(() => {
        if (!user.selectedRole) {
            return undefined;
        }

        let cancelled = false;
        const query = teamMemberSearchInput.trim();

        const runSearch = async () => {
            setTeamMemberSearchLoading(true);
            try {
                const response = query
                    ? await searchCallableUsers({
                        query,
                        currentUserId: user.selectedRole,
                        userRoleUuid: user.selectedRole,
                        roleFilters: ['ACCOUNT_ADMIN', 'CLINIC_CARE_GIVER'],
                        paginationLimit: 12,
                        includeCurrentUser: true
                    })
                    : {
                        users: teamMembers.slice(0, 12)
                    };

                if (cancelled) {
                    return;
                }

                const nextOptions = Array.isArray(response?.users) ? response.users : [];
                setTeamMemberSearchOptions((prev) => {
                    if (
                        selectedAgendaRole &&
                        !nextOptions.some((item) => item.userId === selectedAgendaRole)
                    ) {
                        const selected =
                            teamMembers.find((item) => item.userId === selectedAgendaRole) ||
                            prev.find((item) => item.userId === selectedAgendaRole);
                        return selected ? [selected, ...nextOptions] : nextOptions;
                    }
                    return nextOptions;
                });
            } catch {
                if (!cancelled) {
                    setTeamMemberSearchOptions([]);
                }
            } finally {
                if (!cancelled) {
                    setTeamMemberSearchLoading(false);
                }
            }
        };

        const timeoutId = setTimeout(() => {
            void runSearch();
        }, query ? 250 : 0);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [selectedAgendaRole, teamMemberSearchInput, teamMembers, user.selectedRole]);

    const selectedAgendaMember = useMemo(
        () => teamMembers.find((item) => item.userId === selectedAgendaRole) || null,
        [selectedAgendaRole, teamMembers]
    );
    const teamMemberSearchOptionsById = useMemo(
        () =>
            new Map(
                teamMemberSearchOptions.map((item) => [
                    item.userId,
                    item
                ])
            ),
        [teamMemberSearchOptions]
    );
    const selectedAgendaOption = useMemo(
        () =>
            teamMemberSearchOptionsById.get(selectedAgendaRole) ||
            teamMembers.find((item) => item.userId === selectedAgendaRole) ||
            null,
        [selectedAgendaRole, teamMemberSearchOptionsById, teamMembers]
    );

    const activeRole = useMemo(
        () => (user?.roles || []).find((item) => item.uuid === user?.selectedRole) || null,
        [user?.roles, user?.selectedRole]
    );
    const isOwnAgenda = selectedAgendaRole === user.selectedRole;
    const canEditAvailability = isOwnAgenda || activeRole?.role === 'ACCOUNT_ADMIN';
    const canCreatePrivateNotes = activeRole?.clinician_type === 'PSYCHOLOGY';
    const caregiversById = useMemo(
        () =>
            new Map(
                caregivers.map((item) => [
                    item.userId,
                    item
                ])
            ),
        [caregivers]
    );
    const caregiverSearchOptionsById = useMemo(
        () =>
            new Map(
                caregiverSearchOptions.map((item) => [
                    item.userId,
                    item
                ])
            ),
        [caregiverSearchOptions]
    );
    const selectedCaregiverOption = useMemo(
        () =>
            caregiverSearchOptionsById.get(appointmentForm.caregiver) ||
            caregiversById.get(appointmentForm.caregiver) ||
            null,
        [appointmentForm.caregiver, caregiverSearchOptionsById, caregiversById]
    );

    useEffect(() => {
        let cancelled = false;

        const loadAgendaAvailability = async () => {
            if (!user.selectedRole || !selectedAgendaRole) {
                setSelectedAgendaRoleUserData({});
                setAvailability(defaultAvailability);
                setAvailabilityDraft(defaultAvailability);
                setEditingAvailability(false);
                setAvailabilityError('');
                return;
            }

            setAvailabilityLoading(true);
            setAvailabilityError('');
            setEditingAvailability(false);

            try {
                const response = await fetchUserByRole({
                    userRoleUuid: user.selectedRole,
                    roleUuid: selectedAgendaRole
                });
                if (cancelled) {
                    return;
                }

                const userData = response?.data?.user_data || {};
                const normalizedAvailability = normalizeAvailability(userData);
                setSelectedAgendaRoleUserData(userData);
                setAvailability(normalizedAvailability);
                setAvailabilityDraft(normalizedAvailability);
            } catch (err) {
                if (cancelled) {
                    return;
                }

                setSelectedAgendaRoleUserData({});
                setAvailability(defaultAvailability);
                setAvailabilityDraft(defaultAvailability);
                setAvailabilityError(
                    err?.response?.data?.description ||
                        'Não foi possível carregar os horários desta agenda.'
                );
            } finally {
                if (!cancelled) {
                    setAvailabilityLoading(false);
                }
            }
        };

        void loadAgendaAvailability();

        return () => {
            cancelled = true;
        };
    }, [selectedAgendaRole, user.selectedRole]);

    useEffect(() => {
        let cancelled = false;

        const loadAppointments = async () => {
            if (!user.selectedRole || !selectedAgendaRole) {
                return;
            }

            setLoadingAppointments(true);
            setAppointmentsError('');

            try {
                const response = await listAppointments({
                    userRoleUuid: user.selectedRole,
                    clinicianUuid: selectedAgendaRole,
                    appointmentTypes: ['CALL', 'SCHEDULE'],
                    appointmentStatuses: showCompletedOnly
                        ? ['COMPLETED']
                        : ['SCHEDULED', 'ONGOING'],
                    queryTs: appliedStartDate.toISOString(),
                    order: 'ASC',
                    paginationLimit: 50
                });

                if (cancelled) {
                    return;
                }

                setAppointments(Array.isArray(response?.data) ? response.data : []);
            } catch (err) {
                if (cancelled) {
                    return;
                }

                setAppointments([]);
                setAppointmentsError(
                    err?.response?.data?.description ||
                        'Não foi possível carregar os agendamentos.'
                );
            } finally {
                if (!cancelled) {
                    setLoadingAppointments(false);
                }
            }
        };

        void loadAppointments();

        return () => {
            cancelled = true;
        };
    }, [appliedStartDate, appointmentsReloadKey, selectedAgendaRole, showCompletedOnly, user.selectedRole]);

    const combinedAgenda = useMemo(
        () => buildAgendaFromAppointments(appointments, appliedEndDate),
        [appointments, appliedEndDate]
    );

    const showAppointmentActions = !showCompletedOnly;

    const filteredAgenda = useMemo(() => {
        const normalizedQuery = searchQuery.trim().toLowerCase();

        return combinedAgenda
            .filter(
                (day) =>
                    !day.date.isBefore(appliedStartDate.startOf('day')) &&
                    !day.date.isAfter(appliedEndDate.startOf('day'))
            )
            .map((day) => ({
                ...day,
                entries: day.entries.filter((entry) =>
                    normalizedQuery ? entry.title.toLowerCase().includes(normalizedQuery) : true
                )
            }))
            .filter((day) => day.entries.length > 0);
    }, [appliedEndDate, appliedStartDate, combinedAgenda, searchQuery]);

    const handleMainTabClick = (tab) => {
        if (tab === 'Início') {
            navigate('/');
            return;
        }

        if (tab === 'Agendamentos') {
            navigate('/appointments');
            return;
        }

        if (tab === 'Equipa Técnica') {
            navigate('/doctors');
            return;
        }

        if (tab === 'Cuidadores e Pacientes') {
            navigate('/caregivers');
            return;
        }

        if (tab === 'Documentos') {
            navigate('/documents');
        }
    };

    const handleCalendarDateClick = (value) => {
        if (!value) {
            return;
        }

        const clickedDay = value.startOf('day');
        const { start, end } = calendarRangeDraft;

        if (!start || (start && end)) {
            setCalendarRangeDraft({ start: clickedDay, end: null });
            setCalendarSelectionError('');
            return;
        }

        if (clickedDay.isBefore(start)) {
            setCalendarRangeDraft({ start: clickedDay, end: null });
            setCalendarSelectionError('');
            return;
        }

        const nextLength = rangeLengthInDays(start, clickedDay);
        if (nextLength > 7) {
            setCalendarSelectionError('Pode selecionar no máximo um intervalo de 7 dias.');
            return;
        }

        setCalendarRangeDraft({ start, end: clickedDay });
        setCalendarSelectionError('');
    };

    const handleApplyDateFilter = () => {
        const start = calendarRangeDraft.start?.startOf('day') || dayjs().startOf('day');
        const end = (calendarRangeDraft.end || calendarRangeDraft.start || dayjs()).startOf('day');
        const normalizedStart = end.isBefore(start) ? end : start;
        const normalizedEnd = end.isBefore(start) ? start : end;

        setAppliedStartDate(normalizedStart);
        setAppliedEndDate(normalizedEnd);
        setCalendarSelectionError('');
    };

    const handleClearDateFilter = () => {
        const today = dayjs().startOf('day');
        setCalendarRangeDraft({
            start: today,
            end: today.add(6, 'day')
        });
        setAppliedStartDate(today);
        setAppliedEndDate(today.add(6, 'day'));
        setCalendarSelectionError('');
    };

    const handleToggleDay = (dayKey) => {
        if (!editingAvailability || !canEditAvailability || availabilitySaving || availabilityLoading) {
            return;
        }

        setAvailabilityDraft((prev) => ({
            ...prev,
            activeDays: prev.activeDays.includes(dayKey)
                ? prev.activeDays.filter((item) => item !== dayKey)
                : [...prev.activeDays, dayKey]
        }));
    };

    const handleStartEditingAvailability = () => {
        setAvailabilityError('');
        setAvailabilityDraft(availability);
        setEditingAvailability(true);
    };

    const handleSaveAvailability = async () => {
        if (!user.selectedRole || !selectedAgendaRole || !canEditAvailability) {
            return;
        }

        setAvailabilitySaving(true);
        setAvailabilityError('');

        try {
            const nextUserData = {
                ...selectedAgendaRoleUserData,
                [AVAILABILITY_ACTIVE_DAYS_KEY]: availabilityDraft.activeDays,
                [AVAILABILITY_NOTES_KEY]: availabilityDraft.notes.trim()
            };

            await updateUserByRole({
                userRoleUuid: user.selectedRole,
                payload: {
                    role_uuid: selectedAgendaRole,
                    user_data: nextUserData
                }
            });

            setSelectedAgendaRoleUserData(nextUserData);
            setAvailability(availabilityDraft);
            setEditingAvailability(false);
        } catch (err) {
            setAvailabilityError(
                err?.response?.data?.description ||
                    'Não foi possível guardar os horários da tua agenda.'
            );
        } finally {
            setAvailabilitySaving(false);
        }
    };

    const handleCancelAvailability = () => {
        setAvailabilityError('');
        setAvailabilityDraft(availability);
        setEditingAvailability(false);
    };

    const handleOpenNewAppointment = () => {
        setAppointmentError('');
        setAppointmentSuccess('');
        setCaregiverSearchInput('');
        setCaregiverSearchOptions(caregivers.slice(0, 12));
        setAppointmentForm((prev) => ({
            ...createInitialAppointmentForm(),
            caregiver: caregivers[0]?.userId || prev.caregiver || ''
        }));
        setNewAppointmentDialogOpen(true);
    };

    const handleCloseNewAppointment = () => {
        if (appointmentSubmitting) {
            return;
        }
        setNewAppointmentDialogOpen(false);
        setCaregiverSearchInput('');
        setCaregiverSearchOptions([]);
    };

    const handleCreateAppointment = async () => {
        if (!user.selectedRole || !selectedAgendaRole) {
            setAppointmentError('Não foi possível identificar o profissional do agendamento.');
            return;
        }

        if (!appointmentForm.caregiver) {
            setAppointmentError('Seleciona um cuidador.');
            return;
        }

        const appointmentMoment = dayjs(
            `${appointmentForm.date.format('YYYY-MM-DD')}T${appointmentForm.time}`
        );

        if (!appointmentMoment.isValid()) {
            setAppointmentError('Define uma data e hora válidas.');
            return;
        }

        setAppointmentSubmitting(true);
        setAppointmentError('');
        setAppointmentSuccess('');

        try {
            await createAppointmentAdmin({
                userRoleUuid: user.selectedRole,
                payload: {
                    type: 'SCHEDULE',
                    status: 'SCHEDULED',
                    clinician: selectedAgendaRole,
                    caregiver: appointmentForm.caregiver,
                    ts: appointmentMoment.toISOString(),
                    slot_duration: Number(appointmentForm.duration)
                }
            });

            setAppointmentSuccess('Agendamento criado com sucesso.');
            setNewAppointmentDialogOpen(false);
            setAppointmentForm(createInitialAppointmentForm());
            setAppointmentsReloadKey((prev) => prev + 1);
        } catch (err) {
            setAppointmentError(
                err?.response?.data?.description || 'Não foi possível criar o agendamento.'
            );
        } finally {
            setAppointmentSubmitting(false);
        }
    };

    const availabilityState = editingAvailability ? availabilityDraft : availability;

    const buildAppointmentDetails = (entry, date) => {
        const parsedNames = parseAppointmentNames(entry?.title);
        const caregiverUuid = entry?.caregiver?.uuid || '';
        const caregiverStatus = caregiversById.get(caregiverUuid);
        const caregiverUser = caregiverStatus || (caregiverUuid
            ? {
                  userId: caregiverUuid,
                  displayName: entry?.caregiver?.name || entry?.title || 'Cuidador',
                  role: entry?.caregiver?.role || 'PATIENT_CARE_GIVER',
                  isOnline: false
              }
            : null);
        const isOnline = Boolean(caregiverStatus?.isOnline);
        const canStartCall = Boolean(caregiverUser) && isAppointmentOnCurrentDay(entry);

        return {
            id: entry?.id || entry?.uuid || `${date?.format('YYYY-MM-DD')}-${entry?.start}`,
            headline: parsedNames.headline,
            secondary: parsedNames.secondary,
            quickLabel: formatAppointmentQuickLabel(date, entry?.start, entry?.end),
            dateLabel: date?.locale('pt').format('DD/MM/YYYY') || '',
            timeLabel: entry?.start && entry?.end ? `${entry.start} - ${entry.end}` : '',
            entry,
            isFocused: entry?.uuid === focusAppointmentUuid,
            isOnline,
            canStartCall,
            caregiverUser
        };
    };

    const handleOpenAppointmentNoteDialog = async (detailsOverride = null) => {
        const details = detailsOverride || selectedAppointmentDetails;
        if (!details?.entry || !user.selectedRole) {
            return;
        }

        if (detailsOverride) {
            setSelectedAppointmentDetails(detailsOverride);
        }

        setNoteError('');
        setNotePriviledged(false);
        setNoteContent('');
        setNoteLoading(false);
        setNoteDialogOpen(true);

        if (!details.entry.note) {
            setNoteDialogMode('create');
            return;
        }

        setNoteDialogMode('view');
        setNoteLoading(true);
        try {
            const resp = await fetchClinicalNote({
                userRoleUuid: user.selectedRole,
                noteUuid: details.entry.note
            });
            setNoteContent(resp?.data?.content || '');
        } catch (err) {
            const description = err?.response?.data?.description || '';
            setNoteError(
                description === 'Method not allowed for external user.'
                    ? 'O conteúdo desta nota foi definido como privado.'
                    : description || 'Não foi possível carregar a nota.'
            );
        } finally {
            setNoteLoading(false);
        }
    };

    const handleCloseAppointmentNoteDialog = () => {
        if (noteSubmitting) {
            return;
        }

        setNoteDialogOpen(false);
        setNoteError('');
        setNoteLoading(false);
        setNoteContent('');
        setNotePriviledged(false);
        setNoteDialogMode('create');
    };

    const handleCreateAppointmentNote = async () => {
        if (!user.selectedRole || !selectedAppointmentDetails?.entry || !noteContent.trim()) {
            return;
        }

        setNoteSubmitting(true);
        setNoteError('');

        try {
            await createClinicalNote({
                userRoleUuid: user.selectedRole,
                payload: {
                    ts: new Date().toISOString(),
                    subject: selectedAppointmentDetails.entry.caregiver?.uuid,
                    appointment: selectedAppointmentDetails.entry.uuid,
                    format: 'PLAIN',
                    content: noteContent.trim(),
                    ...(canCreatePrivateNotes ? { priviledged: notePriviledged } : {})
                }
            });

            setNoteDialogOpen(false);
            setAppointmentsReloadKey((prev) => prev + 1);
            setAppointmentSuccess('Nota associada ao agendamento guardada com sucesso.');
            setSelectedAppointmentDetails((prev) =>
                prev
                    ? {
                          ...prev,
                          entry: {
                              ...prev.entry,
                              note: 'created'
                          }
                      }
                    : prev
            );
        } catch (err) {
            setNoteError(
                err?.response?.data?.description || 'Não foi possível guardar a nota.'
            );
        } finally {
            setNoteSubmitting(false);
        }
    };

    const handleOpenRescheduleDialog = (detailsOverride = null) => {
        const details = detailsOverride || selectedAppointmentDetails;
        if (!details?.entry?.ts) {
            return;
        }

        if (detailsOverride) {
            setSelectedAppointmentDetails(detailsOverride);
        }

        const ts = dayjs(details.entry.ts);
        setRescheduleForm({
            date: ts.startOf('day'),
            time: ts.format('HH:mm'),
            duration: Number(details.entry.slotDuration || 45)
        });
        setRescheduleError('');
        setRescheduleDialogOpen(true);
    };

    const handleCloseRescheduleDialog = () => {
        if (rescheduleSubmitting) {
            return;
        }
        setRescheduleDialogOpen(false);
        setRescheduleError('');
    };

    const handleConfirmReschedule = async () => {
        if (!user.selectedRole || !selectedAppointmentDetails?.entry?.uuid) {
            return;
        }

        const appointmentMoment = dayjs(
            `${rescheduleForm.date.format('YYYY-MM-DD')}T${rescheduleForm.time}`
        );

        if (!appointmentMoment.isValid()) {
            setRescheduleError('Define uma data e hora válidas.');
            return;
        }

        setRescheduleSubmitting(true);
        setRescheduleError('');

        try {
            await updateAppointmentAdmin({
                userRoleUuid: user.selectedRole,
                payload: {
                    appointment: selectedAppointmentDetails.entry.uuid,
                    status: 'CANCELLED'
                }
            });

            await createAppointmentAdmin({
                userRoleUuid: user.selectedRole,
                payload: {
                    type: 'SCHEDULE',
                    status: 'SCHEDULED',
                    clinician:
                        selectedAppointmentDetails.entry.clinician?.uuid || selectedAgendaRole,
                    caregiver: selectedAppointmentDetails.entry.caregiver?.uuid,
                    ts: appointmentMoment.toISOString(),
                    slot_duration: Number(rescheduleForm.duration)
                }
            });

            setRescheduleDialogOpen(false);
            setSelectedAppointmentDetails(null);
            setAppointmentsReloadKey((prev) => prev + 1);
            setAppointmentSuccess('Agendamento reagendado com sucesso.');
        } catch (err) {
            setRescheduleError(
                err?.response?.data?.description || 'Não foi possível reagendar o agendamento.'
            );
        } finally {
            setRescheduleSubmitting(false);
        }
    };

    const handleStartAppointmentCall = (detailsOverride = null) => {
        const details = detailsOverride || selectedAppointmentDetails;
        if (!details?.canStartCall || !details?.caregiverUser) {
            return;
        }

        if (detailsOverride) {
            setSelectedAppointmentDetails(detailsOverride);
        }

        navigate(`/call/${details.caregiverUser.userId}`, {
            state: {
                autoDial: true,
                user: details.caregiverUser,
                appointmentUuid: details.entry.uuid,
                wakeIfOffline: true
            }
        });
    };

    const handleOpenStatusDialog = (detailsOverride = null) => {
        const details = detailsOverride || selectedAppointmentDetails;
        if (!details?.entry?.uuid) {
            return;
        }

        if (detailsOverride) {
            setSelectedAppointmentDetails(detailsOverride);
        }

        setManualStatusValue(details.entry.status || 'SCHEDULED');
        setStatusError('');
        setStatusDialogOpen(true);
    };

    const handleCloseStatusDialog = () => {
        if (statusSubmitting) {
            return;
        }

        setStatusDialogOpen(false);
        setStatusError('');
    };

    const handleConfirmStatusUpdate = async () => {
        if (!user.selectedRole || !selectedAppointmentDetails?.entry?.uuid || !manualStatusValue) {
            return;
        }

        setStatusSubmitting(true);
        setStatusError('');

        const nowIso = new Date().toISOString();
        const payload = {
            appointment: selectedAppointmentDetails.entry.uuid,
            status: manualStatusValue
        };

        if (manualStatusValue === 'ONGOING') {
            payload.ti = nowIso;
        }

        if (manualStatusValue === 'COMPLETED') {
            payload.tf = nowIso;
        }

        try {
            await updateAppointmentAdmin({
                userRoleUuid: user.selectedRole,
                payload
            });

            const statusLabel = getAppointmentStatusMeta(manualStatusValue).label;
            setSelectedAppointmentDetails((prev) =>
                prev
                    ? {
                          ...prev,
                          entry: {
                              ...prev.entry,
                              status: manualStatusValue
                          }
                      }
                    : prev
            );
            setStatusDialogOpen(false);
            setAppointmentsReloadKey((prev) => prev + 1);
            setAppointmentSuccess(`Estado do agendamento atualizado para "${statusLabel}".`);
        } catch (err) {
            setStatusError(
                err?.response?.data?.description ||
                    'Não foi possível atualizar o estado do agendamento.'
            );
        } finally {
            setStatusSubmitting(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt">
            <Box
                sx={{
                    px: { xs: 1.5, md: 2 },
                    pb: { xs: 1.5, md: 2 },
                    pt: 0,
                    bgcolor: '#FFFFFF'
                }}
            >
                <Box sx={{ mt: 0 }}>
                    <DashboardTabs
                        tabs={dashboardTabs}
                        onTabClick={handleMainTabClick}
                        activeTab="Agendamentos"
                    />
                </Box>

                <Paper
                    variant="elevation=0"
                    sx={{
                        p: { xs: 1.8, md: 2.4 },
                        bgcolor: '#FFFFFF',
                        borderRadius: '20px',
                        boxShadow: 'none'
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center" mb={2.5}>
                        <Box>
                            <Typography variant="h4" fontWeight={700} color="#3D3D3D">
                                Agenda
                            </Typography>
                        </Box>
                    </Stack>

                    {appointmentSuccess ? (
                        <Alert
                            sx={{
                                mb: 2,
                                borderRadius: '14px',
                                bgcolor: 'rgba(214, 55, 49, 0.06)',
                                color: '#3D3D3D',
                                '& .MuiAlert-icon': {
                                    color: '#D63731'
                                }
                            }}
                        >
                            {appointmentSuccess}
                        </Alert>
                    ) : null}

                    {appointmentsError ? (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 2,
                                borderRadius: '14px'
                            }}
                        >
                            {appointmentsError}
                        </Alert>
                    ) : null}

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                lg: 'minmax(0, 3fr) minmax(280px, 1fr)'
                            },
                            gap: 2.2,
                            alignItems: 'start'
                        }}
                    >
                        <Box>
                            <Stack
                                direction={{ xs: 'column', xl: 'row' }}
                                spacing={1.4}
                                justifyContent="space-between"
                                alignItems={{ xs: 'stretch', xl: 'center' }}
                                mb={2}
                            >
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.4}>
                                    <Autocomplete
                                        options={teamMemberSearchOptions}
                                        value={selectedAgendaOption}
                                        inputValue={teamMemberSearchInput}
                                        loading={teamMemberSearchLoading}
                                        onInputChange={(_, value) => {
                                            setTeamMemberSearchInput(value);
                                        }}
                                        onChange={(_, value) => setSelectedAgendaRole(value?.userId || '')}
                                        isOptionEqualToValue={(option, value) =>
                                            option?.userId === value?.userId
                                        }
                                        getOptionLabel={(option) => option?.displayName || option?.userId || ''}
                                        noOptionsText={
                                            teamMemberSearchInput.trim()
                                                ? 'Sem membros para esta pesquisa.'
                                                : 'Sem membros disponíveis.'
                                        }
                                        loadingText="A procurar membros..."
                                        sx={{
                                            minWidth: { xs: '100%', md: 260 },
                                            '& .MuiOutlinedInput-root': {
                                                minHeight: 48,
                                                borderRadius: '14px',
                                                bgcolor: '#FFFFFF'
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Minha Agenda"
                                            />
                                        )}
                                    />

                                    <Tooltip title="Criar novo agendamento">
                                        <Button
                                            variant="contained"
                                            startIcon={<AddCircleRoundedIcon />}
                                            onClick={handleOpenNewAppointment}
                                            sx={primaryButtonSx}
                                        >
                                        Nova Marcação
                                        </Button>
                                    </Tooltip>

                                    <Tooltip title="Mostrar apenas agendamentos concluídos">
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={showCompletedOnly}
                                                    onChange={(event) =>
                                                        setShowCompletedOnly(event.target.checked)
                                                    }
                                                    color="error"
                                                />
                                            }
                                            label={
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={700}
                                                    color="#3D3D3D"
                                                >
                                                Concluídos
                                                </Typography>
                                            }
                                            sx={{
                                                ml: 0.2,
                                                mr: 0,
                                                px: 1.4,
                                                minHeight: 48,
                                                borderRadius: '14px',
                                                bgcolor: '#FFFFFF'
                                            }}
                                        />
                                    </Tooltip>
                                </Stack>

                                <TextField
                                    placeholder="Procurar"
                                    value={searchQuery}
                                    onChange={(event) => setSearchQuery(event.target.value)}
                                    sx={{
                                        minWidth: { xs: '100%', xl: 250 },
                                        '& .MuiOutlinedInput-root': {
                                            minHeight: 48,
                                            borderRadius: '14px',
                                            bgcolor: '#FFFFFF'
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <SearchRoundedIcon sx={{ color: '#D63731' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Stack>

                            <Paper
                                sx={{
                                    px: 2.2,
                                    py: 1.1,
                                    display: 'grid',
                                    gridTemplateColumns: '160px minmax(0, 1fr)',
                                    gap: 2,
                                    bgcolor: '#3D3D3D',
                                    color: '#fff',
                                    borderRadius: '10px',
                                    boxShadow: 'none'
                                }}
                            >
                                <Typography variant="caption" fontWeight={600}>
                                    Data
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: showAppointmentActions
                                            ? '170px minmax(0, 1fr) 150px'
                                            : '170px minmax(0, 1fr)',
                                        gap: 2.2,
                                        alignItems: 'center'
                                    }}
                                >
                                    <Typography variant="caption" fontWeight={600}>
                                        Hora
                                    </Typography>
                                    <Typography variant="caption" fontWeight={600}>
                                        Cuidador
                                    </Typography>
                                    {showAppointmentActions ? (
                                        <Typography variant="caption" fontWeight={600}>
                                            Ações
                                        </Typography>
                                    ) : null}
                                </Box>
                            </Paper>

                            <Stack spacing={1.2} mt={1.2}>
                                {loadingAppointments ? (
                                    <Paper
                                        sx={{
                                            px: 2.2,
                                            py: 2.2,
                                            bgcolor: '#ECECEC',
                                            borderRadius: '14px',
                                            boxShadow: 'none'
                                        }}
                                    >
                                        <Typography
                                            fontWeight={600}
                                            color="#3D3D3D"
                                            textAlign="center"
                                        >
                                            A carregar agendamentos...
                                        </Typography>
                                    </Paper>
                                ) : filteredAgenda.length === 0 ? (
                                    <Paper
                                        sx={{
                                            px: 2.2,
                                            py: 2.2,
                                            bgcolor: '#ECECEC',
                                            borderRadius: '14px',
                                            boxShadow: 'none'
                                        }}
                                    >
                                        <Typography
                                            fontWeight={600}
                                            color="#3D3D3D"
                                            textAlign="center"
                                        >
                                            Sem marcações para o intervalo e pesquisa selecionados.
                                        </Typography>
                                    </Paper>
                                ) : (
                                    filteredAgenda.map((day) => (
                                        <Paper
                                            key={day.date.format('YYYY-MM-DD')}
                                            sx={{
                                                px: 2.2,
                                                py: 1.35,
                                                display: 'grid',
                                                gridTemplateColumns: '160px minmax(0, 1fr)',
                                                gap: 2,
                                                alignItems: 'start',
                                                bgcolor: '#ECECEC',
                                                borderRadius: '14px',
                                                boxShadow: 'none'
                                            }}
                                        >
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: '1.75rem',
                                                        fontWeight: 700,
                                                        lineHeight: 1,
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'keep-all',
                                                        color: '#3D3D3D'
                                                    }}
                                                >
                                                    {formatAgendaDayTitle(day.date)}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        mt: 0.4,
                                                        fontSize: '0.78rem',
                                                        fontWeight: 600,
                                                        color: '#6B6B6B'
                                                    }}
                                                >
                                                    {formatAgendaWeekday(day.date)}
                                                </Typography>
                                            </Box>

                                            <Stack spacing={0.85}>
                                                {day.entries.map((entry, index) => {
                                                    const statusMeta = getAppointmentStatusMeta(entry.status);
                                                    const entryDetails = buildAppointmentDetails(entry, day.date);

                                                    return (
                                                        <Paper
                                                            key={`${day.date.format('YYYY-MM-DD')}-entry-${index}`}
                                                            sx={{
                                                                px: 1.6,
                                                                py: 1.15,
                                                                display: 'grid',
                                                                gridTemplateColumns:
                                                                    showAppointmentActions
                                                                        ? '170px minmax(0, 1fr) 150px 170px'
                                                                        : '170px minmax(0, 1fr) 150px',
                                                                gap: 2.2,
                                                                alignItems: 'center',
                                                                borderRadius: '12px',
                                                                bgcolor: entryDetails.isFocused
                                                                    ? 'rgba(214, 55, 49, 0.08)'
                                                                    : 'rgba(255,255,255,0.55)',
                                                                border: entryDetails.isFocused
                                                                    ? '1.5px solid #D63731'
                                                                    : '1px solid #E0E0E0',
                                                                boxShadow: 'none',
                                                                cursor: 'default'
                                                            }}
                                                            >
                                                                <Typography
                                                                variant="body2"
                                                                fontWeight={700}
                                                                color="#3D3D3D"
                                                            >
                                                                {entry.start} - {entry.end}
                                                            </Typography>

                                                            <Stack
                                                                direction="row"
                                                                spacing={1}
                                                                alignItems="center"
                                                                sx={{ minWidth: 0 }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: 10,
                                                                        height: 10,
                                                                        borderRadius: '50%',
                                                                        bgcolor: entryDetails.isOnline
                                                                            ? '#2E7D32'
                                                                            : '#D63731',
                                                                        flexShrink: 0
                                                                    }}
                                                                />
                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight={700}
                                                                    color="#3D3D3D"
                                                                    sx={{ minWidth: 0 }}
                                                                >
                                                                    {entry.title}
                                                                </Typography>
                                                            </Stack>

                                                                <Chip
                                                                    label={statusMeta.label}
                                                                    size="small"
                                                                sx={{
                                                                    width: 118,
                                                                    justifyContent: 'center',
                                                                    fontWeight: 700,
                                                                    borderRadius: '10px',
                                                                        ...statusMeta.sx
                                                                    }}
                                                                />

                                                                {showAppointmentActions ? (
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={0.8}
                                                                    justifyContent="flex-end"
                                                                    onClick={(event) => event.stopPropagation()}
                                                                >
                                                                    <Tooltip title="Notas do agendamento">
                                                                        <Button
                                                                            variant="outlined"
                                                                            onClick={() => {
                                                                                void handleOpenAppointmentNoteDialog(entryDetails);
                                                                            }}
                                                                            sx={{
                                                                                minWidth: 40,
                                                                                width: 40,
                                                                                height: 40,
                                                                                p: 0,
                                                                                borderRadius: '50%',
                                                                                bgcolor: '#FFFFFF',
                                                                                color: '#3D3D3D',
                                                                                borderColor: '#3D3D3D',
                                                                                borderWidth: '1.5px',
                                                                                boxShadow: 'none',
                                                                                '&:hover': {
                                                                                    bgcolor: '#F7F7F7',
                                                                                    borderColor: '#3D3D3D',
                                                                                    boxShadow: 'none'
                                                                                }
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                component="img"
                                                                                src="/assets/logos-svg/notes.svg"
                                                                                alt="Notas"
                                                                                sx={{ width: 18, height: 18 }}
                                                                            />
                                                                        </Button>
                                                                    </Tooltip>

                                                                    <Tooltip title="Reagendar">
                                                                        <Button
                                                                            variant="contained"
                                                                            onClick={() => handleOpenRescheduleDialog(entryDetails)}
                                                                            sx={{
                                                                                minWidth: 40,
                                                                                width: 40,
                                                                                height: 40,
                                                                                p: 0,
                                                                                borderRadius: '50%',
                                                                                bgcolor: '#6B6B6B',
                                                                                color: '#FFFFFF',
                                                                                boxShadow: 'none',
                                                                                '&:hover': {
                                                                                    bgcolor: '#4F4F4F',
                                                                                    boxShadow: 'none'
                                                                                }
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                component="img"
                                                                                src="/assets/logos-svg/reschedule.svg"
                                                                                alt="Reagendar"
                                                                                sx={{ width: 18, height: 18 }}
                                                                            />
                                                                        </Button>
                                                                    </Tooltip>

                                                                    <Tooltip title="Gerir estado do agendamento">
                                                                        <Button
                                                                            variant="contained"
                                                                            onClick={() =>
                                                                                handleOpenStatusDialog(entryDetails)
                                                                            }
                                                                            sx={{
                                                                                minWidth: 40,
                                                                                width: 40,
                                                                                height: 40,
                                                                                p: 0,
                                                                                borderRadius: '50%',
                                                                                bgcolor: '#5E984B',
                                                                                color: '#FFFFFF',
                                                                                boxShadow: 'none',
                                                                                '&:hover': {
                                                                                    bgcolor: '#4D7F3D',
                                                                                    boxShadow: 'none'
                                                                                }
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                component="img"
                                                                                src="/assets/logos-svg/manageAppoint.svg"
                                                                                alt="Gerir estado"
                                                                                sx={{ width: 18, height: 18 }}
                                                                            />
                                                                        </Button>
                                                                    </Tooltip>

                                                                    <Tooltip
                                                                        title={
                                                                            entryDetails.canStartCall
                                                                                ? 'Iniciar videochamada'
                                                                                : 'A videochamada só ficará disponível no dia do agendamento.'
                                                                        }
                                                                    >
                                                                        <span>
                                                                            <Button
                                                                                variant="contained"
                                                                                onClick={() =>
                                                                                    handleStartAppointmentCall(
                                                                                        entryDetails
                                                                                    )
                                                                                }
                                                                                disabled={!entryDetails.canStartCall}
                                                                                sx={{
                                                                                    minWidth: 40,
                                                                                    width: 40,
                                                                                    height: 40,
                                                                                    p: 0,
                                                                                    borderRadius: '50%',
                                                                                    bgcolor: '#D63731',
                                                                                    color: '#FFFFFF',
                                                                                    boxShadow: 'none',
                                                                                    '&:hover': {
                                                                                        bgcolor: '#BF2F2A',
                                                                                        boxShadow: 'none'
                                                                                    },
                                                                                    '&.Mui-disabled': {
                                                                                        bgcolor: '#D0D0D0',
                                                                                        color: '#FFFFFF'
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <Box
                                                                                    component="img"
                                                                                    src="/assets/logos-svg/call.svg"
                                                                                    alt="Videochamada"
                                                                                    sx={{ width: 18, height: 18 }}
                                                                                />
                                                                            </Button>
                                                                        </span>
                                                                    </Tooltip>
                                                                </Stack>
                                                                ) : null}
                                                            </Paper>
                                                    );
                                                })}
                                            </Stack>
                                        </Paper>
                                    ))
                                )}
                            </Stack>
                        </Box>

                        <Stack spacing={1.8}>
                            <Paper
                                sx={{
                                    p: 2,
                                    borderRadius: '18px',
                                    bgcolor: '#FFFFFF',
                                    boxShadow: 'none',
                                    border: '1px solid #E5E5E5'
                                }}
                            >
                                <Typography variant="body2" fontWeight={700} color="#3D3D3D">
                                    Data
                                </Typography>

                                <Box
                                    sx={{
                                        borderRadius: '14px',
                                        overflow: 'hidden',
                                        bgcolor: '#FFFFFF'
                                    }}
                                >
                                    <DateCalendar
                                        value={calendarRangeDraft.end || calendarRangeDraft.start}
                                        onChange={handleCalendarDateClick}
                                        slots={{ day: AppointmentRangeDay }}
                                        slotProps={{
                                            day: {
                                                rangeStart: calendarRangeDraft.start,
                                                rangeEnd:
                                                    calendarRangeDraft.end || calendarRangeDraft.start
                                            }
                                        }}
                                        sx={{
                                            width: '100%',
                                            '& .MuiPickersCalendarHeader-root': {
                                                px: 1
                                            },
                                            '& .MuiDayCalendar-weekDayLabel': {
                                                color: '#8A8A8A',
                                                fontWeight: 600
                                            }
                                        }}
                                    />

                                    <Box px={1.4}>
                                        {calendarSelectionError ? (
                                            <Typography
                                                variant="caption"
                                                sx={{ display: 'block', mt: 0.5, color: '#D63731' }}
                                            >
                                                {calendarSelectionError}
                                            </Typography>
                                        ) : null}
                                    </Box>

                                    <Stack direction="row" spacing={1.2}>
                                        <Tooltip title="Limpar filtro de datas">
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                onClick={handleClearDateFilter}
                                                sx={secondaryButtonSx}
                                            >
                                                Limpar
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Aplicar filtro de datas">
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                onClick={handleApplyDateFilter}
                                                sx={primaryButtonSx}
                                            >
                                                Aplicar
                                            </Button>
                                        </Tooltip>
                                    </Stack>
                                </Box>

                            </Paper>

                            <Paper
                                sx={{
                                    p: 2,
                                    borderRadius: '18px',
                                    bgcolor: '#FFFFFF',
                                    boxShadow: 'none',
                                    border: '1px solid #E5E5E5'
                                }}
                            >
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={1.2}
                                >
                                    <Typography variant="body2" fontWeight={700} color="#3D3D3D">
                                        Meus horários
                                    </Typography>
                                    <Stack direction="row" spacing={0.5}>
                                        {editingAvailability ? (
                                            <>
                                                <Tooltip title="Cancelar edição dos horários">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleCancelAvailability}
                                                            disabled={availabilitySaving}
                                                            sx={{
                                                                width: 34,
                                                                height: 34,
                                                                bgcolor: '#F3F3F3',
                                                                color: '#6B6B6B',
                                                                '&:hover': { bgcolor: '#E8E8E8' }
                                                            }}
                                                        >
                                                            <CloseRoundedIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Guardar horários">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleSaveAvailability}
                                                            disabled={availabilitySaving}
                                                            sx={{
                                                                width: 34,
                                                                height: 34,
                                                                bgcolor: '#D63731',
                                                                color: '#FFFFFF',
                                                                '&:hover': { bgcolor: '#BF2F2A' }
                                                            }}
                                                        >
                                                            <CheckRoundedIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </>
                                        ) : canEditAvailability ? (
                                            <Tooltip title="Editar horários">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleStartEditingAvailability}
                                                        disabled={availabilityLoading}
                                                        sx={{
                                                            width: 38,
                                                            height: 38,
                                                            bgcolor: '#D63731',
                                                            color: '#FFFFFF',
                                                            '&:hover': { bgcolor: '#BF2F2A' },
                                                            '&.Mui-disabled': {
                                                                bgcolor: '#D0D0D0',
                                                                color: '#F7F7F7'
                                                            }
                                                        }}
                                                    >
                                                        <Box
                                                            component="img"
                                                            src="/assets/logos-svg/edit.svg"
                                                            alt="Editar"
                                                            sx={{ width: 16, height: 16, display: 'block' }}
                                                        />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        ) : null}
                                    </Stack>
                                </Stack>

                                {availabilityError ? (
                                    <Alert
                                        severity="error"
                                        sx={{
                                            mb: 1.4,
                                            borderRadius: '12px'
                                        }}
                                    >
                                        {availabilityError}
                                    </Alert>
                                ) : null}

                                {availabilityLoading ? (
                                    <Alert
                                        severity="info"
                                        sx={{
                                            mb: 1.4,
                                            borderRadius: '12px',
                                            bgcolor: 'rgba(214, 55, 49, 0.05)',
                                            color: '#3D3D3D',
                                            '& .MuiAlert-icon': {
                                                color: '#D63731'
                                            }
                                        }}
                                    >
                                        A carregar horários...
                                    </Alert>
                                ) : null}

                                <Stack
                                    direction="row"
                                    spacing={0.8}
                                    flexWrap="wrap"
                                    useFlexGap
                                    mb={1.4}
                                >
                                    {weekdayOptions.map((day) => {
                                        const active = availabilityState.activeDays.includes(day.key);
                                        return (
                                            <Button
                                                key={day.key}
                                                variant="outlined"
                                                onClick={() => handleToggleDay(day.key)}
                                                sx={{
                                                    minWidth: 0,
                                                    px: 1.1,
                                                    py: 0.5,
                                                    borderRadius: '10px',
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    fontSize: '0.74rem',
                                                    color: active ? '#FFFFFF' : '#5E5E5E',
                                                    bgcolor: active ? '#D63731' : '#FFFFFF',
                                                    borderColor: active ? '#D63731' : '#D9D9D9',
                                                    pointerEvents:
                                                        editingAvailability &&
                                                        canEditAvailability &&
                                                        !availabilitySaving &&
                                                        !availabilityLoading
                                                            ? 'auto'
                                                            : 'none',
                                                    '&:hover': {
                                                        bgcolor: active ? '#BF2F2A' : '#F7F7F7',
                                                        borderColor: active ? '#BF2F2A' : '#D9D9D9'
                                                    }
                                                }}
                                            >
                                                {day.label}
                                            </Button>
                                        );
                                    })}
                                </Stack>

                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    value={availabilityState.notes}
                                    onChange={(event) =>
                                        setAvailabilityDraft((prev) => ({
                                            ...prev,
                                            notes: event.target.value.slice(0, 180)
                                        }))
                                    }
                                    disabled={
                                        !editingAvailability ||
                                        !canEditAvailability ||
                                        availabilitySaving ||
                                        availabilityLoading
                                    }
                                    placeholder="Adiciona notas breves sobre o teu horário."
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '14px',
                                            bgcolor: '#FFFFFF'
                                        }
                                    }}
                                />
                            </Paper>
                        </Stack>
                    </Box>
                </Paper>

                <Dialog
                    open={newAppointmentDialogOpen}
                    onClose={handleCloseNewAppointment}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{
                        sx: {
                            borderRadius: '22px',
                            boxShadow: '0px 10px 30px rgba(61, 61, 61, 0.12)'
                        }
                    }}
                >
                    <DialogTitle
                        sx={{ px: 3, pt: 3, pb: 1.5, fontWeight: 700, color: '#3D3D3D' }}
                    >
                        Nova Marcação
                    </DialogTitle>
                    <DialogContent sx={{ px: 3, pb: 1, pt: 0.5 }}>
                        <Stack spacing={2}>
                            {appointmentError ? (
                                <Alert
                                    severity="error"
                                    sx={{
                                        borderRadius: '12px'
                                    }}
                                >
                                    {appointmentError}
                                </Alert>
                            ) : null}

                            <Box
                                sx={{
                                    p: 1.6,
                                    borderRadius: '16px',
                                    bgcolor: 'rgba(214, 55, 49, 0.05)'
                                }}
                            >
                                <Typography variant="body2" fontWeight={700} color="#3D3D3D">
                                    Profissional
                                </Typography>
                                <Typography variant="body2" color="#6B6B6B" mt={0.4}>
                                    {selectedAgendaMember?.displayName || selectedAgendaRole}
                                </Typography>
                            </Box>

                            <Autocomplete
                                options={caregiverSearchOptions}
                                value={selectedCaregiverOption}
                                inputValue={caregiverSearchInput}
                                loading={caregiverSearchLoading}
                                onInputChange={(_, value) => {
                                    setCaregiverSearchInput(value);
                                }}
                                onChange={(_, value) =>
                                    setAppointmentForm((prev) => ({
                                        ...prev,
                                        caregiver: value?.userId || ''
                                    }))
                                }
                                isOptionEqualToValue={(option, value) =>
                                    option?.userId === value?.userId
                                }
                                getOptionLabel={(option) => option?.displayName || option?.userId || ''}
                                noOptionsText={
                                    caregiverSearchInput.trim()
                                        ? 'Sem cuidadores para esta pesquisa.'
                                        : 'Sem cuidadores disponíveis.'
                                }
                                loadingText="A procurar cuidadores..."
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Cuidador"
                                        placeholder="Pesquisar cuidador"
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '14px'
                                            }
                                        }}
                                    />
                                )}
                            />

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.6}>
                                <DatePicker
                                    label="Data"
                                    format="DD/MM/YYYY"
                                    value={appointmentForm.date}
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            setAppointmentForm((prev) => ({
                                                ...prev,
                                                date: newValue.startOf('day')
                                            }));
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            sx: {
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '14px'
                                                }
                                            }
                                        }
                                    }}
                                />

                                <TextField
                                    label="Hora"
                                    type="time"
                                    value={appointmentForm.time}
                                    onChange={(event) =>
                                        setAppointmentForm((prev) => ({
                                            ...prev,
                                            time: event.target.value
                                        }))
                                    }
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ step: 300 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '14px'
                                        }
                                    }}
                                />
                            </Stack>

                            <TextField
                                select
                                label="Duração"
                                value={appointmentForm.duration}
                                onChange={(event) =>
                                    setAppointmentForm((prev) => ({
                                        ...prev,
                                        duration: Number(event.target.value)
                                    }))
                                }
                                fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '14px'
                                    }
                                }}
                            >
                                {durationOptions.map((minutes) => (
                                    <MenuItem key={minutes} value={minutes}>
                                        {minutes} minutos
                                    </MenuItem>
                                ))}
                            </TextField>

                            <Typography variant="caption" color="#6B6B6B">
                                O agendamento será criado com o estado{' '}
                                <strong>AGENDADO</strong>.
                            </Typography>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={handleCloseNewAppointment}
                            disabled={appointmentSubmitting}
                            sx={secondaryButtonSx}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleCreateAppointment}
                            disabled={appointmentSubmitting}
                            sx={primaryButtonSx}
                        >
                            {appointmentSubmitting ? 'A guardar...' : 'Criar Agendamento'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={noteDialogOpen}
                    onClose={handleCloseAppointmentNoteDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: dialogPaperSx }}
                >
                    <DialogTitle sx={dialogTitleSx}>
                        {noteDialogMode === 'view' ? 'Nota do agendamento' : 'Criar nota do agendamento'}
                    </DialogTitle>
                    <DialogContent sx={dialogContentSx}>
                        {noteError ? <Alert severity="error">{noteError}</Alert> : null}
                        <TextField
                            fullWidth
                            multiline
                            minRows={5}
                            label="Nota"
                            placeholder="Escreve aqui a nota..."
                            value={noteContent}
                            onChange={(event) => setNoteContent(event.target.value)}
                            disabled={noteDialogMode === 'view' || noteLoading || noteSubmitting}
                        />
                        {canCreatePrivateNotes && noteDialogMode !== 'view' ? (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={notePriviledged}
                                        onChange={(event) => setNotePriviledged(event.target.checked)}
                                        color="error"
                                    />
                                }
                                label="Nota privada"
                            />
                        ) : null}
                        {noteDialogMode === 'view' && !noteLoading && !noteError ? (
                            <Typography variant="caption" color="#6B6B6B">
                                Esta nota está associada ao agendamento selecionado.
                            </Typography>
                        ) : null}
                    </DialogContent>
                    <DialogActions sx={dialogActionsSx}>
                        <Button
                            onClick={handleCloseAppointmentNoteDialog}
                            disabled={noteSubmitting}
                            variant="outlined"
                            sx={secondaryButtonSx}
                        >
                            {noteDialogMode === 'view' ? 'Fechar' : 'Cancelar'}
                        </Button>
                        {noteDialogMode !== 'view' ? (
                            <Button
                                variant="contained"
                                onClick={handleCreateAppointmentNote}
                                disabled={noteSubmitting || noteLoading || !noteContent.trim()}
                                sx={primaryButtonSx}
                            >
                                {noteSubmitting ? 'A guardar...' : 'Guardar'}
                            </Button>
                        ) : null}
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={rescheduleDialogOpen}
                    onClose={handleCloseRescheduleDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: dialogPaperSx }}
                >
                    <DialogTitle sx={dialogTitleSx}>Reagendar marcação</DialogTitle>
                    <DialogContent sx={dialogContentSx}>
                        {rescheduleError ? <Alert severity="error">{rescheduleError}</Alert> : null}
                        <Stack
                            spacing={1}
                            sx={{
                                p: 1.6,
                                borderRadius: '16px',
                                bgcolor: 'rgba(214, 55, 49, 0.05)'
                            }}
                        >
                            <Typography variant="body2" fontWeight={700} color="#3D3D3D">
                                Cuidador
                            </Typography>
                            <Typography variant="body2" color="#6B6B6B">
                                {selectedAppointmentDetails?.entry?.caregiver?.name || '--'}
                            </Typography>
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.6}>
                            <DatePicker
                                label="Data"
                                format="DD/MM/YYYY"
                                value={rescheduleForm.date}
                                onChange={(newValue) => {
                                    if (newValue) {
                                        setRescheduleForm((prev) => ({
                                            ...prev,
                                            date: newValue.startOf('day')
                                        }));
                                    }
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '14px'
                                            }
                                        }
                                    }
                                }}
                            />
                            <TextField
                                label="Hora"
                                type="time"
                                value={rescheduleForm.time}
                                onChange={(event) =>
                                    setRescheduleForm((prev) => ({
                                        ...prev,
                                        time: event.target.value
                                    }))
                                }
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 300 }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '14px'
                                    }
                                }}
                            />
                        </Stack>
                        <TextField
                            select
                            label="Duração"
                            value={rescheduleForm.duration}
                            onChange={(event) =>
                                setRescheduleForm((prev) => ({
                                    ...prev,
                                    duration: Number(event.target.value)
                                }))
                            }
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '14px'
                                }
                            }}
                        >
                            {durationOptions.map((minutes) => (
                                <MenuItem key={minutes} value={minutes}>
                                    {minutes} minutos
                                </MenuItem>
                            ))}
                        </TextField>
                        <Typography variant="caption" color="#6B6B6B">
                            O agendamento atual será cancelado e será criada uma nova marcação com o novo horário.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={dialogActionsSx}>
                        <Button
                            onClick={handleCloseRescheduleDialog}
                            disabled={rescheduleSubmitting}
                            variant="outlined"
                            sx={secondaryButtonSx}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleConfirmReschedule}
                            disabled={rescheduleSubmitting}
                            sx={primaryButtonSx}
                        >
                            {rescheduleSubmitting ? 'A reagendar...' : 'Guardar alterações'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={statusDialogOpen}
                    onClose={handleCloseStatusDialog}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{ sx: dialogPaperSx }}
                >
                    <DialogTitle sx={dialogTitleSx}>Gerir estado do agendamento</DialogTitle>
                    <DialogContent sx={dialogContentSx}>
                        {statusError ? <Alert severity="error">{statusError}</Alert> : null}
                        <Typography variant="body2" color="#6B6B6B">
                            {selectedAppointmentDetails?.headline || 'Agendamento'}
                        </Typography>
                        <TextField
                            select
                            fullWidth
                            label="Estado"
                            value={manualStatusValue}
                            onChange={(event) => setManualStatusValue(event.target.value)}
                            disabled={statusSubmitting}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '14px'
                                }
                            }}
                        >
                            {['SCHEDULED', 'ONGOING', 'COMPLETED', 'CANCELLED'].map((status) => (
                                <MenuItem key={status} value={status}>
                                    {getAppointmentStatusMeta(status).label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Typography variant="caption" color="#8A8A8A">
                            Ao marcar como "Em curso" ou "Realizada", hora atual
                            é utilizada como referência.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={dialogActionsSx}>
                        <Button
                            onClick={handleCloseStatusDialog}
                            disabled={statusSubmitting}
                            variant="outlined"
                            sx={secondaryButtonSx}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleConfirmStatusUpdate}
                            disabled={statusSubmitting}
                            sx={primaryButtonSx}
                        >
                            {statusSubmitting ? 'A guardar...' : 'Guardar estado'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
};

export default Appointments;
